<!--
  Shared Setup bulk Excel upload shell (Chart of Accounts, Task List, Information Manager).
  Path: modules/financialclose/components/SetupBulkUpload/
  Uses BulkUploadFrame for Step 1 / Step 2 UX; parents own parse, validate, submit,
  and supply the preview table via #preview. Distinct from Manager BulkUpload
  (managed checklist / TB / project IR modes).
-->
<template>
  <div :data-testid="testIds.root || undefined">
    <slot name="trigger">
      <v-btn
        v-if="showTrigger"
        color="secondaryBtn"
        :class="triggerClass"
        :data-testid="testIds.openButton"
        @click="open"
      >
        <v-icon v-if="buttonIcon" class="pr-2">{{ buttonIcon }}</v-icon>
        <img
          v-else-if="buttonImage"
          :src="buttonImage"
          alt=""
          class="pr-2"
          aria-hidden="true"
        >
        <span>{{ buttonLabel }}</span>
      </v-btn>
    </slot>

    <lb-drawer
      v-if="showContainer"
      :value="isOpen"
      :Maxwidth="resolvedMaxWidth"
      :heading="heading"
      :loading="surfaceLoading"
      :data-testid="testIds.drawer"
      @input="onDrawerInput"
    >
      <template #body>
        <BulkUploadFrame
          ref="frame"
          v-bind="frameBindings"
          @download="$emit('download')"
          @reupload="onReupload"
          @files-change="$emit('files-change', $event)"
          @reupload-file="onReuploadFile"
          @update:showErrorsOnly="$emit('update:showErrorsOnly', $event)"
        >
          <template #preview>
            <slot name="preview" />
          </template>
        </BulkUploadFrame>
      </template>

      <template #actions>
        <slot name="actions-leading">
          <v-spacer />
        </slot>
        <v-btn
          outlined
          class="bulk-import-dialog__btn text-none secondaryBtn"
          :data-testid="testIds.cancel"
          @click="onCancel"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          class="bulk-import-dialog__btn text-none white--text"
          :disabled="!canSubmit"
          :loading="submitLoading"
          :data-testid="testIds.upload"
          @click="$emit('upload')"
        >
          {{ submitLabel }}
        </v-btn>
      </template>
    </lb-drawer>
  </div>
</template>

<script>
import BulkUploadFrame from '../BulkUpload/BulkUploadFrame.vue';
import { drawerWidths } from '../../../../styles/design-tokens.js';

export default {
  name: 'SetupBulkUpload',
  components: {
    BulkUploadFrame,
  },
  props: {
    /** External open state (Task List). When omitted and showTrigger, open is internal. */
    value: {
      type: Boolean,
      default: undefined,
    },
    showTrigger: {
      type: Boolean,
      default: false,
    },
    /** Keep drawer mounted while closed (default). Set false to unmount when closed. */
    keepMounted: {
      type: Boolean,
      default: true,
    },
    buttonLabel: {
      type: String,
      default: 'Bulk Upload',
    },
    buttonIcon: {
      type: String,
      default: 'mdi-file-document-plus-outline',
    },
    buttonImage: {
      type: String,
      default: '',
    },
    triggerClass: {
      type: String,
      default: 'mr-1',
    },
    heading: {
      type: String,
      default: 'Bulk Upload',
    },
    step1Description: {
      type: String,
      default: '',
    },
    step2Title: {
      type: String,
      default: 'Step 2: Upload File',
    },
    step2Description: {
      type: String,
      default: '',
    },
    hasPreview: {
      type: Boolean,
      default: false,
    },
    validating: {
      type: Boolean,
      default: false,
    },
    fileName: {
      type: String,
      default: '',
    },
    showSelectedFileName: {
      type: Boolean,
      default: true,
    },
    reuploadVisible: {
      type: Boolean,
      default: false,
    },
    file: {
      type: [Array, Object, File],
      default: null,
    },
    resetDropzone: {
      type: Boolean,
      default: false,
    },
    fileAccept: {
      type: String,
      default: '.xlsx,.xls,.xlsm,.xlsb',
    },
    hideDropzoneDetails: {
      type: Boolean,
      default: true,
    },
    singleFile: {
      type: Boolean,
      default: false,
    },
    dropzoneAccept: {
      type: String,
      default: '',
    },
    showInlineErrors: {
      type: Boolean,
      default: false,
    },
    inlineErrorText: {
      type: String,
      default: '',
    },
    inlineErrorNote: {
      type: String,
      default: '',
    },
    headerErrorMessages: {
      type: Array,
      default: () => [],
    },
    /** Plain string messages under "Data Errors" (no Row N prefix). */
    dataErrorMessages: {
      type: Array,
      default: () => [],
    },
    rowErrors: {
      type: Array,
      default: () => [],
    },
    footerVisible: {
      type: Boolean,
      default: false,
    },
    errorFooterVisible: {
      type: Boolean,
      default: false,
    },
    errorFooterText: {
      type: String,
      default: '',
    },
    errorFooterNote: {
      type: String,
      default: '',
    },
    footerSummary: {
      type: Array,
      default: () => [],
    },
    summaryTight: {
      type: Boolean,
      default: false,
    },
    errorFilterVisible: {
      type: Boolean,
      default: false,
    },
    showErrorsOnly: {
      type: Boolean,
      default: false,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    canSubmit: {
      type: Boolean,
      default: false,
    },
    submitLabel: {
      type: String,
      default: 'Upload',
    },
    maxWidth: {
      type: String,
      default: '',
    },
    /**
     * Shell element test ids. Callers pass full ids to preserve existing
     * Setup contracts (do not force Manager BulkUpload prefix suffixes).
     */
    testIds: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      internalOpen: false,
      drawerWidths,
      isClosing: false,
    };
  },
  computed: {
    isExternallyControlled() {
      return this.value !== undefined;
    },
    isOpen() {
      return this.isExternallyControlled ? !!this.value : this.internalOpen;
    },
    showContainer() {
      return this.keepMounted || this.isOpen;
    },
    resolvedMaxWidth() {
      if (this.maxWidth) return this.maxWidth;
      return this.hasPreview ? this.drawerWidths.expanded : this.drawerWidths.lg;
    },
    surfaceLoading() {
      return this.busy && this.hasPreview;
    },
    submitLoading() {
      return this.busy && this.hasPreview;
    },
    resolvedStep2Title() {
      return this.hasPreview ? 'Preview' : this.step2Title;
    },
    frameBindings() {
      return {
        testIds: this.testIds,
        step1Description: this.step1Description,
        step2Title: this.resolvedStep2Title,
        step2Description: this.step2Description,
        hasPreview: this.hasPreview,
        validating: this.validating,
        selectedFileName: this.fileName,
        showSelectedFileName: this.showSelectedFileName,
        showReupload: this.reuploadVisible,
        file: this.file,
        resetTrigger: this.resetDropzone,
        fileAccept: this.fileAccept,
        hideDropzoneDetails: this.hideDropzoneDetails,
        singleFile: this.singleFile,
        dropzoneAccept: this.dropzoneAccept,
        showInlineErrors: this.showInlineErrors,
        inlineErrorText: this.inlineErrorText,
        inlineErrorNote: this.inlineErrorNote,
        headerErrorMessages: this.headerErrorMessages,
        rowErrors: this.rowErrors,
        dataErrorMessages: this.showInlineErrors ? this.dataErrorMessages : [],
        showPreviewFooter: this.footerVisible,
        showErrorFooter: this.errorFooterVisible,
        errorFooterText: this.errorFooterText,
        errorFooterNote: this.errorFooterNote,
        summaryErrorMessages: this.footerSummary,
        summaryTight: this.summaryTight,
        showErrorFilter: this.errorFilterVisible,
        showErrorsOnly: this.showErrorsOnly,
      };
    },
  },
  methods: {
    open() {
      if (this.isExternallyControlled) {
        this.$emit('input', true);
        this.$emit('open');
        return;
      }
      this.internalOpen = true;
      this.$emit('open');
    },
    close() {
      if (this.isExternallyControlled) {
        this.$emit('input', false);
      } else {
        this.internalOpen = false;
      }
    },
    closeAndCancel() {
      if (!this.isOpen || this.isClosing) return;
      this.isClosing = true;
      this.close();
      this.$emit('cancel');
      this.$nextTick(() => {
        this.isClosing = false;
      });
    },
    onDrawerInput(next) {
      const open = !!next;
      if (!open) {
        this.closeAndCancel();
        return;
      }
      if (this.isExternallyControlled) {
        this.$emit('input', true);
      } else {
        this.internalOpen = true;
      }
    },
    onCancel() {
      this.closeAndCancel();
    },
    onReupload() {
      this.$emit('reupload');
      this.$nextTick(() => {
        this.$refs.frame?.openFilePicker();
      });
    },
    onReuploadFile(event) {
      const files = event?.target?.files;
      if (!files?.length) return;
      this.$emit('files-change', [files[0]]);
      this.$emit('file-selected', files[0]);
      if (event.target) event.target.value = '';
    },
    /** Imperative open for parents that hold a ref (matches Manager BulkUpload). */
    openBulkUpload() {
      this.open();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../BulkUpload/BulkUpload.scss';
</style>
